import { z } from 'zod';
import { apiGet, apiSend } from '@/lib/api';

/**
 * USTAWIENIA AUTOMATU (`SetupData`) — zakładka „Ustawienia" karty automatu i ekran całej wyspy.
 *
 * ⚠️ Typ to litera SPS: tak/nie, liczba, napis, katalog, plik. Nieznaną literę serwis zamienia na
 * napis, więc schemat może być ścisły — inna wartość znaczyłaby rozjazd z serwisem, nie z SPS.
 */
const TypSchema = z.enum(['B', 'I', 'S', 'D', 'F']);

const ZapisSchema = z.object({ mozliwy: z.boolean(), wymagana: z.string(), starsze: z.array(z.string()) });

const AutomatySchema = z.array(z.object({ vmid: z.number(), nazwa: z.string() }));

const UstawienieSchema = z.object({
  param: z.string(),
  opis: z.string().nullable(),
  typ: TypSchema,
  grupa: z.string().nullable(),
  wartosc: z.string().nullable(),
  wKolejce: z.boolean(),
  naWszystkich: z.boolean(),
  inneNa: z.array(z.object({ vmid: z.number(), wartosc: z.string().nullable() })),
  /** Ile automatów MA ten parametr. `optional`: starsza instancja tego pola nie odda. */
  automatow: z.number().optional(),
});

export type Ustawienie = z.infer<typeof UstawienieSchema>;

const UstawieniaAutomatuSchema = z.object({
  vmid: z.number(),
  automaty: AutomatySchema,
  zapis: ZapisSchema,
  ustawienia: z.array(UstawienieSchema),
});

export type UstawieniaAutomatu = z.infer<typeof UstawieniaAutomatuSchema>;

export const fetchUstawieniaAutomatu = (groupId: string, vmid: number) =>
  apiGet(`/ustawienia?groupId=${encodeURIComponent(groupId)}&vmid=${vmid}`, UstawieniaAutomatuSchema);

const UstawienieWyspySchema = z.object({
  param: z.string(),
  opis: z.string().nullable(),
  typ: TypSchema,
  grupa: z.string().nullable(),
  naWszystkich: z.boolean(),
  /** Tylko automaty, które ten parametr MAJĄ. */
  wartosci: z.array(z.object({ vmid: z.number(), wartosc: z.string().nullable(), wKolejce: z.boolean() })),
});

export type UstawienieWyspy = z.infer<typeof UstawienieWyspySchema>;

const UstawieniaWyspySchema = z.object({
  automaty: AutomatySchema,
  zapis: ZapisSchema,
  ustawienia: z.array(UstawienieWyspySchema),
});

/** `param` — tylko ten jeden parametr (ekran grup artykułów, `GRAPHIC_SELECT_ENABLED`). */
export const fetchUstawieniaWyspy = (groupId: string, param?: string) =>
  apiGet(
    `/ustawienia/wyspa?groupId=${encodeURIComponent(groupId)}${param === undefined ? '' : `&param=${encodeURIComponent(param)}`}`,
    UstawieniaWyspySchema,
  );

/**
 * ZAPIS USTAWIENIA — na wskazanych automatach albo na „wszystkich".
 *
 * ⚠️ „WSZYSTKIE" ROZSTRZYGA SERWER: każdy automat, który ten parametr ma. Ekran nie wysyła listy
 * automatów wyspy, bo jeden automat bez parametru kończył wtedy cały zapis odmową.
 */
export const zapiszUstawienie = (
  groupId: string,
  param: string,
  wartosc: string,
  zakres: { vmids: number[] } | { wszystkie: true },
) =>
  apiSend(
    'POST',
    '/ustawienia',
    { groupId, param, wartosc, ...zakres },
    z.object({ opId: z.string(), automatow: z.number() }),
  );
